import { useEffect, useRef } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useCohortDefinitionStore } from '@verantos-dev/app-state-cohort-definition';
import { useCohortUsageQuery } from '@verantos-dev/data-access-cohorts';
import type { LegacyConceptGroup } from '@verantos-dev/types-concepts';

import { useCohortDefinitionLifecycle } from '../../hooks/initialization/useCohortDefinitionLifecycle';
import { useConceptGroupsQuery } from '../api/useConceptGroupsQuery';

interface UseCohortHydrationParams {
  projectId: string;
  cohortId: string;
  cohortVersionId: string;
}

/**
 * Gen-3 cohort detail hydration. Replaces the legacy
 * `useCohortBuilderPage` → `useCohortBuilderPagePreFetch` chain on the
 * gen-3 path. Pure TanStack + Zustand — no RTK Query, no Redux.
 *
 * Composes:
 * - `useCohortDefinitionLifecycle` (existing) — TanStack reads for the
 *   cohort version + profile, hydrates the Zustand
 *   cohort-definition store, resets on param change, owns the socket
 *   plumbing.
 * - `useConceptGroupsQuery` (new) — TanStack read for project concept
 *   groups, hydrated into the same Zustand store.
 *
 * Returns the loading/error shape the page consumes for skeletons + the
 * hard-fail overlay. `isDefinitionLoading` is exposed separately from the
 * combined `isLoading` (definition + concept groups) so a consumer that
 * only reads definition data (e.g. Study period) isn't held back by the
 * project-wide concept-groups fetch.
 */
export const useCohortHydration = ({
  projectId,
  cohortId,
  cohortVersionId,
}: UseCohortHydrationParams) => {
  const setConceptGroups = useCohortDefinitionStore((s) => s.setConceptGroups);
  const setUsedInAnalyticalDatasets = useCohortDefinitionStore(
    (s) => s.setUsedInAnalyticalDatasets
  );
  const setAssetEditWhileInUseEnabled = useCohortDefinitionStore(
    (s) => s.setAssetEditWhileInUseEnabled
  );

  const { isLoading: isDefinitionLoading, error: definitionError } = useCohortDefinitionLifecycle({
    projectId,
    cohortId,
    cohortVersionId,
    enabled: !!(projectId && cohortId && cohortVersionId),
  });

  // Track which analytical datasets reference this cohort (OR-6192) — same
  // hydration the legacy `useCohortProfileData` does for the v0 page. Folds
  // into `selectIsEditable`: when `asset_edit_while_in_use` is off this locks
  // the cohort read-only across every editing surface (server guard on
  // `upsert_draft_version` is the authoritative backstop); when on it only
  // blocks reprocessing while a dependent AD is PROCESSING.
  const { data: usage } = useCohortUsageQuery({ projectId, cohortId });
  const usageDatasets = usage?.analyticalDatasets;

  // TODO: cleanup with asset_edit_while_in_use ff — hydrate the flag into the
  // store so the pure `selectIsEditable` can decide whether AD usage locks the
  // cohort read-only (flag off) or leaves it editable (flag on).
  const isAssetEditWhileInUseEnabled = Boolean(useFeatureFlagSplit('asset_edit_while_in_use'));

  useEffect(() => {
    setUsedInAnalyticalDatasets(usageDatasets ?? []);
  }, [usageDatasets, setUsedInAnalyticalDatasets]);

  useEffect(() => {
    setAssetEditWhileInUseEnabled(isAssetEditWhileInUseEnabled);
  }, [isAssetEditWhileInUseEnabled, setAssetEditWhileInUseEnabled]);

  const {
    data: conceptGroups,
    isLoading: isConceptGroupsLoading,
    isError: isConceptGroupsError,
  } = useConceptGroupsQuery({ projectId });

  // Hydrate concept groups into the Zustand store on every fresh fetch.
  // Use a ref to avoid re-hydrating on equivalent identity churn — the
  // server returns a new array reference each fetch, but the data is
  // typically stable.
  const lastHydratedRef = useRef<typeof conceptGroups>(undefined);
  useEffect(() => {
    if (!conceptGroups || conceptGroups === lastHydratedRef.current) return;
    lastHydratedRef.current = conceptGroups;
    setConceptGroups(conceptGroups as LegacyConceptGroup[]);
  }, [conceptGroups, setConceptGroups]);

  return {
    isDefinitionLoading: !!isDefinitionLoading,
    isLoading: !!(isDefinitionLoading || isConceptGroupsLoading),
    isError: !!definitionError || isConceptGroupsError,
  };
};
